import React from "react";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import Divider from "@material-ui/core/Divider";
import { Checkbox, ListItemText, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(() => ({
  select: {
    minWidth: 120,
    maxWidth: 190,
  },
}));

// Estética unificada de filtros (misma que el filtro "Estado...").
const filterSelectStyle = {
  fontSize: 11,
  height: 22,
  border: "1px solid rgba(0,0,0,0.18)",
  borderRadius: 10,
  padding: "0 4px",
  background: "transparent",
  opacity: 0.7,
};

const TicketsQueueSelect = ({
  userQueues = [],
  selectedQueueIds = [],
  onChange,
  showAllTickets = false,
  showOrphans = true,
  canSeeUnassigned = false,
  isAdmin = false,
  onToggleShowAll,
  onToggleShowOrphans,
}) => {
  const classes = useStyles();
  const showOrphansItem = isAdmin || canSeeUnassigned;
  const isLocked = showAllTickets;

  // "Sin asignar" cuenta como una opción más del filtro.
  const totalOptions = userQueues.length + (showOrphansItem ? 1 : 0);
  const orphansSelected = showOrphans && showOrphansItem;
  const selectedCount = selectedQueueIds.length + (orphansSelected ? 1 : 0);
  const allSelected = showAllTickets || (totalOptions > 0 && selectedCount === totalOptions);

  // Nombres de lo seleccionado (Sin asignar + departamentos), para label y tooltip.
  const getSelectedNames = () => {
    const names = [];
    if (orphansSelected) names.push(i18n.t("ticketsList.filters.unassigned"));
    userQueues.forEach(q => {
      if (selectedQueueIds.includes(q.id)) names.push(q.name);
    });
    return names;
  };

  const buildLabel = () => {
    // 0 seleccionados o TODOS seleccionados → placeholder.
    if (allSelected || selectedCount === 0) return i18n.t("ticketsList.filters.queuePlaceholder");
    // 1 seleccionado → su nombre.
    if (selectedCount === 1) return getSelectedNames()[0];
    // 2+ (parcial) → "N departamentos".
    return i18n.t("ticketsList.filters.queueCount", { count: selectedCount });
  };

  const handleClickItem = (e, action) => {
    e.stopPropagation();
    action();
  };

  const handleQueueToggle = (queueId) => {
    if (isLocked) return;
    const newIds = selectedQueueIds.includes(queueId)
      ? selectedQueueIds.filter(id => id !== queueId)
      : [...selectedQueueIds, queueId];
    onChange(newIds);
  };

  // Tooltip (filtro cerrado): solo en selección parcial → nombres uno por línea.
  // 0 seleccionados o todos → sin tooltip (title vacío = MUI no lo muestra).
  const tooltipTitle = (allSelected || selectedCount === 0)
    ? ""
    : (
      <div style={{ fontSize: 12, lineHeight: 1.5 }}>
        {getSelectedNames().map((n, i) => (
          <div key={i}>{n}</div>
        ))}
      </div>
    );

  return (
    <Tooltip title={tooltipTitle} arrow placement="bottom">
    <FormControl size="small" className={classes.select}>
      <Select
        multiple
        value={[]}
        displayEmpty
        disableUnderline
        renderValue={() => <span style={{ fontSize: 11 }}>{buildLabel()}</span>}
        style={filterSelectStyle}
        MenuProps={{
          getContentAnchorEl: null,
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          PaperProps: { style: { maxHeight: 320 } },
        }}
        inputProps={{ style: { padding: "0 4px", fontSize: 11 } }}
      >
        {isAdmin && (
          <MenuItem dense onClick={e => handleClickItem(e, onToggleShowAll)}>
            <Checkbox size="small" checked={showAllTickets} color="primary" />
            <ListItemText primary={i18n.t("ticketsList.filters.all")} primaryTypographyProps={{ style: { fontSize: 13, fontWeight: 600 } }} />
          </MenuItem>
        )}
        {isAdmin && <Divider />}
        {showOrphansItem && (
          <MenuItem dense disabled={isLocked} onClick={e => handleClickItem(e, onToggleShowOrphans)}>
            <Checkbox size="small" checked={isLocked ? true : showOrphans} color="primary" style={{ opacity: isLocked ? 0.5 : 1 }} />
            <ListItemText primary={i18n.t("ticketsList.filters.unassigned")} primaryTypographyProps={{ style: { fontSize: 13 } }} />
          </MenuItem>
        )}
        {/* Separador entre "Sin asignar" y los departamentos reales */}
        {showOrphansItem && userQueues.length > 0 && <Divider style={{ margin: "4px 0" }} />}
        {userQueues.map(queue => (
          <MenuItem dense key={queue.id} disabled={isLocked} onClick={() => handleQueueToggle(queue.id)}>
            <Checkbox
              size="small"
              checked={isLocked ? true : selectedQueueIds.includes(queue.id)}
              style={{ color: isLocked ? undefined : queue.color, opacity: isLocked ? 0.5 : 1 }}
            />
            <ListItemText primary={queue.name} primaryTypographyProps={{ style: { fontSize: 13 } }} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    </Tooltip>
  );
};

export default TicketsQueueSelect;
